import React, { useState, useEffect } from 'react';
import { Calendar, User, Clock, Filter, ChevronLeft, ChevronRight, AlertCircle, Coffee, Car, Heart, Briefcase, Home } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { db } from '../firebase/config';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';

const TeamAvailability = () => {
  const { currentUser } = useAuth();
  const { users, departments, teams } = useData();
  const [personalTasks, setPersonalTasks] = useState([]);
  const [selectedView, setSelectedView] = useState('day'); // day/week/month
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterType, setFilterType] = useState('all'); // all/team/department
  const [loading, setLoading] = useState(true);

  // אייקונים למשימות אישיות
  const taskIcons = {
    'appointment': { icon: Heart, color: 'text-red-500' },
    'personal': { icon: Coffee, color: 'text-brown-500' },
    'vacation': { icon: Home, color: 'text-blue-500' },
    'meeting': { icon: Briefcase, color: 'text-purple-500' },
    'other': { icon: Car, color: 'text-gray-500' }
  };

const getSubordinateUsers = () => {
  if (!currentUser || !users || users.length === 0) return [];
  
  console.log('=== חיפוש המשתמש הנוכחי ===');
  console.log('currentUser מ-Auth:', {
    email: currentUser.email,
    uid: currentUser.uid,
    role: currentUser.role
  });
  
  // מחפש את המשתמש הנוכחי לפי email
  let currentUserDoc = users.find(u => 
    u.email && currentUser.email && 
    u.email.toLowerCase() === currentUser.email.toLowerCase()
  );
  
  // פתרון למנכ"ל או org_admin שלא נמצאים
  if (!currentUserDoc) {
    console.warn('לא נמצא המשתמש ברשימת המשתמשים');
    
    // בדיקה לפי email או role
    if (currentUser.email === 'nadav@darkcrypto.com' || 
        currentUser.role === 'org_admin' || 
        currentUser.role === 'ceo' ||
        localStorage.getItem('userRole') === 'org_admin' ||
        localStorage.getItem('userRole') === 'ceo') {
      
      console.log('זוהה כמנכ"ל/מנהל ראשי - מחזיר את כל המשתמשים');
      // מנכ"ל רואה את כולם
      return users;
    }
    
    // אם זה לא מנכ"ל, תחזיר רשימה ריקה
    console.error('המשתמש לא נמצא ואין לו הרשאות מנכ"ל');
    return [];
  }
  
  console.log('משתמש נוכחי נמצא:', {
    name: currentUserDoc.name,
    role: currentUserDoc.role,
    departmentId: currentUserDoc.departmentId,
    teamId: currentUserDoc.teamId
  });
  
  // החזרת עובדים לפי תפקיד
  switch(currentUserDoc.role) {
    case 'ceo':
    case 'org_admin':
      // מנכ"ל ומנהל ראשי רואים את כולם
      console.log('מנכ"ל/מנהל ראשי - מחזיר את כל המשתמשים');
      return users.filter(u => u.id !== currentUserDoc.id);
    
    case 'deputy_ceo':
      // סמנכ"ל רואה את כולם חוץ ממנכ"ל
      return users.filter(u => 
        u.id !== currentUserDoc.id && 
        u.role !== 'ceo' &&
        u.role !== 'org_admin'
      );
    
    case 'dept_manager':
      // מנהל מחלקה רואה רק את המחלקה שלו
      return users.filter(u => 
        u.id !== currentUserDoc.id && 
        u.departmentId === currentUserDoc.departmentId &&
        ['team_manager', 'employee'].includes(u.role)
      );
    
    case 'team_manager':
      // מנהל צוות רואה רק את הצוות שלו
      return users.filter(u => 
        u.teamId === currentUserDoc.teamId &&
        u.role === 'employee' &&
        u.id !== currentUserDoc.id
      );
    
    default:
      console.log('אין הרשאות');
      return [];
  }
};

  // טעינת משימות אישיות של הצוות
  useEffect(() => {
    if (!currentUser || !users || users.length === 0) return;
    
    const loadPersonalTasks = async () => {
      setLoading(true);
      
      // מי המנהל יכול לראות לפי התפקיד שלו
      const subordinateUsers = getSubordinateUsers();
      
      console.log('=== דיבאג טעינת משימות ===');
      console.log('מספר עובדים כפופים:', subordinateUsers.length);
      console.log('עובדים כפופים:', subordinateUsers.map(u => ({
        name: u.name,
        id: u.id,
        email: u.email,
        teamId: u.teamId,
        role: u.role
      })));
      
      if (subordinateUsers.length === 0) {
        setLoading(false);
        setPersonalTasks([]);
        return;
      }
      
      // שאילתה לכל המשימות האישיות
      const q = query(collection(db, 'tasks'));
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        console.log(`סה"כ משימות במערכת: ${snapshot.docs.length}`);
        
        const tasks = [];
        snapshot.docs.forEach(doc => {
          const taskData = { id: doc.id, ...doc.data() };
          
          // בדיקה אם זו משימה אישית
          const isPersonalTask = taskData.scope === 'personal' || 
                                taskData.type === 'personal';
          
          if (!isPersonalTask) return;
          
          // מוצא את בעל המשימה
          let taskOwner = null;
          
          // נסה למצוא לפי assigneeId
          if (taskData.assigneeId) {
            taskOwner = subordinateUsers.find(u => u.id === taskData.assigneeId);
          }
          
          // נסה למצוא לפי assigneeEmail
          if (!taskOwner && taskData.assigneeEmail) {
            taskOwner = subordinateUsers.find(u => u.email === taskData.assigneeEmail);
          }
          
          // נסה למצוא לפי createdBy/creatorId (למשימות שהעובד יצר לעצמו)
          if (!taskOwner && (taskData.createdBy || taskData.creatorId)) {
            const creatorId = taskData.creatorId || taskData.createdBy;
            taskOwner = subordinateUsers.find(u => u.id === creatorId);
            
            // וודא שהמשימה מוקצית לאותו אדם שיצר אותה
            if (taskOwner && taskData.assigneeId && taskData.assigneeId !== creatorId) {
              taskOwner = null; // זו לא משימה אישית של העובד
            }
          }
          
          if (taskOwner) {
            console.log(`✅ נמצאה משימה אישית: "${taskData.title}" של ${taskOwner.name}`);
            console.log(`   תאריך: ${taskData.dueDate}, שעה: ${taskData.dueTime}`);
            tasks.push(taskData);
          }
        });
        
        console.log(`=== סיכום: ${tasks.length} משימות אישיות של העובדים הכפופים ===`);
        setPersonalTasks(tasks);
        setLoading(false);
      });
      
      return () => unsubscribe();
    };
    
    loadPersonalTasks();
  }, [currentUser, users]);

  // מסנן משימות לפי התאריך הנבחר
  const getTasksForDate = (date) => {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);
    
    return personalTasks.filter(task => {
      if (!task.dueDate) return false;
      
      let taskDate;
      // טיפול בפורמטים שונים של תאריך
      if (task.dueDate && task.dueDate.toDate) {
        taskDate = task.dueDate.toDate();
      } else if (task.dueDate && task.dueDate.seconds) {
        taskDate = new Date(task.dueDate.seconds * 1000);
      } else if (typeof task.dueDate === 'string') {
        taskDate = new Date(task.dueDate);
      } else if (task.dueDate instanceof Date) {
        taskDate = task.dueDate;
      } else {
        console.warn('פורמט תאריך לא מזוהה:', task.dueDate);
        return false;
      }
      
      // בדיקה שהתאריך תקין
      if (isNaN(taskDate.getTime())) {
        console.warn('תאריך לא תקין:', task.dueDate);
        return false;
      }
      
      return taskDate >= startOfDay && taskDate <= endOfDay;
    });
  };

  // מסנן משתמשים לפי סוג הסינון
  const getFilteredUsers = () => {
    const subordinates = getSubordinateUsers();
    const currentUserDoc = users.find(u => u.email === currentUser?.email);
    
    if (!currentUserDoc) return subordinates;
    
    switch(filterType) {
      case 'team':
        if (!currentUserDoc.teamId) return subordinates;
        return subordinates.filter(u => u.teamId === currentUserDoc.teamId);
      
      case 'department':
        if (!currentUserDoc.departmentId) return subordinates;
        return subordinates.filter(u => u.departmentId === currentUserDoc.departmentId);
      
      default:
        return subordinates;
    }
  };

  // ניווט בתאריכים
  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    
    switch(selectedView) {
      case 'day':
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 1 : -1));
        break;
      case 'week':
        newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
        break;
      case 'month':
        newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
        break;
    }
    
    setSelectedDate(newDate);
  };

  // תצוגה יומית
  const DayView = () => {
    const tasksToday = getTasksForDate(selectedDate);
    const filteredUsers = getFilteredUsers();
    
    console.log('תצוגה יומית:', {
      date: selectedDate.toLocaleDateString('he-IL'),
      tasksCount: tasksToday.length,
      usersCount: filteredUsers.length
    });
    
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">
          {selectedDate.toLocaleDateString('he-IL', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </h3>
        
        {filteredUsers.length === 0 ? (
          <div className="text-center py-8">
            <AlertCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-500">אין עובדים להצגה</p>
            <p className="text-sm text-gray-400 mt-2">
              ודא שאתה מוגדר כמנהל ושיש עובדים תחתיך
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredUsers.map(user => {
              const userTasks = tasksToday.filter(task => {
                // בדיקה מקיפה של שייכות המשימה למשתמש
                return task.assigneeId === user.id ||
                       task.assigneeEmail === user.email ||
                       (task.createdBy === user.id && task.scope === 'personal') ||
                       (task.creatorId === user.id && task.scope === 'personal');
              });
              
              return (
                <div key={user.id} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <User className="h-5 w-5 text-gray-400" />
                      <span className="font-medium">{user.name}</span>
                      <span className="text-sm text-gray-500">
                        ({user.role === 'employee' ? 'עובד' : 
                          user.role === 'team_manager' ? 'מנהל צוות' : 
                          user.role})
                      </span>
                    </div>
                    {userTasks.length > 0 && (
                      <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs">
                        {userTasks.length} משימות אישיות
                      </span>
                    )}
                  </div>
                  
                  {userTasks.length > 0 ? (
                    <div className="space-y-2 mt-3">
                      {userTasks.map(task => {
                        const IconComponent = taskIcons[task.category]?.icon || Coffee;
                        const iconColor = taskIcons[task.category]?.color || 'text-gray-500';
                        
                        return (
                          <div key={task.id} className="flex items-center gap-3 bg-gray-50 p-2 rounded">
                            <IconComponent className={`h-4 w-4 ${iconColor}`} />
                            <span className="text-sm font-medium">{task.title}</span>
                            {task.dueTime && (
                              <span className="text-xs text-gray-500 mr-auto">
                                {task.dueTime}
                              </span>
                            )}
                            {task.description && (
                              <span className="text-xs text-gray-400">
                                ({task.description})
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-green-600 mt-2">זמין לעבודה ✓</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // תצוגה שבועית
  const WeekView = () => {
    const startOfWeek = new Date(selectedDate);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
    
    const weekDays = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      return date;
    });
    
    const filteredUsers = getFilteredUsers();
    
    if (filteredUsers.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow p-8">
          <div className="text-center">
            <AlertCircle className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-500">אין עובדים להצגה</p>
            <p className="text-sm text-gray-400 mt-2">
              ודא שאתה מוגדר כמנהל ושיש עובדים תחתיך
            </p>
          </div>
        </div>
      );
    }
    
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="grid grid-cols-8 border-b">
          <div className="p-3 font-semibold bg-gray-50">עובד</div>
          {weekDays.map((day, index) => (
            <div key={index} className="p-3 text-center border-r">
              <div className="font-semibold text-sm">
                {day.toLocaleDateString('he-IL', { weekday: 'short' })}
              </div>
              <div className="text-xs text-gray-500">
                {day.toLocaleDateString('he-IL', { day: 'numeric', month: 'numeric' })}
              </div>
            </div>
          ))}
        </div>
        
        <div className="divide-y">
          {filteredUsers.map(user => (
            <div key={user.id} className="grid grid-cols-8">
              <div className="p-3 font-medium bg-gray-50">
                {user.name}
              </div>
              {weekDays.map((day, index) => {
                const dayTasks = getTasksForDate(day).filter(task => {
                  return task.assigneeId === user.id ||
                         task.assigneeEmail === user.email ||
                         (task.createdBy === user.id && task.scope === 'personal') ||
                         (task.creatorId === user.id && task.scope === 'personal');
                });
                
                return (
                  <div key={index} className="p-3 text-center border-r relative group">
                    {dayTasks.length > 0 ? (
                      <div className="space-y-1">
                        {dayTasks.map(task => {
                          const IconComponent = taskIcons[task.category]?.icon || Coffee;
                          const iconColor = taskIcons[task.category]?.color || 'text-gray-500';
                          
                          return (
                            <div key={task.id} className="flex justify-center">
                              <IconComponent className={`h-4 w-4 ${iconColor}`} />
                              {/* Tooltip עם פרטי המשימה */}
                              <div className="absolute hidden group-hover:block bottom-full left-1/2 transform -translate-x-1/2 mb-2 w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                                <div className="font-semibold">{task.title}</div>
                                {task.dueTime && <div>שעה: {task.dueTime}</div>}
                                {task.description && <div className="mt-1">{task.description}</div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <span className="text-green-500">✓</span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // תצוגה חודשית (לעת עתה משתמשת בתצוגה היומית)
  const MonthView = () => {
    return <DayView />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">לוח זמינות הצוות</h1>
        <p className="text-gray-600 mt-1">ניהול וצפייה במשימות האישיות של הצוות</p>
      </div>

      {/* כלי בקרה */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          {/* בחירת תצוגה */}
          <div className="flex gap-2">
            <button
              onClick={() => setSelectedView('day')}
              className={`px-4 py-2 rounded ${
                selectedView === 'day' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              יומי
            </button>
            <button
              onClick={() => setSelectedView('week')}
              className={`px-4 py-2 rounded ${
                selectedView === 'week' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              שבועי
            </button>
            <button
              onClick={() => setSelectedView('month')}
              className={`px-4 py-2 rounded ${
                selectedView === 'month' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 hover:bg-gray-200'
              }`}
            >
              חודשי
            </button>
          </div>

          {/* ניווט בתאריכים */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigateDate('prev')}
              className="p-2 hover:bg-gray-100 rounded"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
            
            <button
              onClick={() => setSelectedDate(new Date())}
              className="px-4 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
            >
              היום
            </button>
            
            <button
              onClick={() => navigateDate('next')}
              className="p-2 hover:bg-gray-100 rounded"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          </div>

          {/* סינון */}
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-500" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border rounded"
            >
              <option value="all">כל העובדים</option>
              <option value="team">הצוות שלי</option>
              <option value="department">המחלקה שלי</option>
            </select>
          </div>
        </div>
      </div>

      {/* תצוגת התוכן */}
      {selectedView === 'day' && <DayView />}
      {selectedView === 'week' && <WeekView />}
      {selectedView === 'month' && <MonthView />}
      
      {/* הסבר על האייקונים */}
      <div className="mt-6 bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-semibold mb-2">מקרא:</h4>
        <div className="flex flex-wrap gap-4 text-sm">
          {Object.entries(taskIcons).map(([type, { icon: Icon, color }]) => (
            <div key={type} className="flex items-center gap-1">
              <Icon className={`h-4 w-4 ${color}`} />
              <span>{
                type === 'appointment' ? 'פגישה רפואית' :
                type === 'personal' ? 'אישי' :
                type === 'vacation' ? 'חופש' :
                type === 'meeting' ? 'פגישה' :
                'אחר'
              }</span>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default TeamAvailability;